import { useState, useEffect } from "react";
import { Menu, X, Globe } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useLang } from "./LangContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_prodimmo-media/artifacts/rb17en5i_logo_prodimmo-removebg-preview.png";

const sectionIds = ["apropos", "prestations", "portfolio", "processus", "tarifs", "faq", "contact"];

export default function Navbar() {
  const { lang, t, toggleLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const navLinks = sectionIds.map((id) => ({
    href: `#${id}`,
    label: t.nav[id],
  }));

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Active section tracking with IntersectionObserver
  useEffect(() => {
    if (location.pathname !== "/") return;
    const observers = [];
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id);
        },
        { rootMargin: "-40% 0px -55% 0px" }
      );
      observer.observe(el);
      observers.push(observer);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [location.pathname]);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + href);
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a href="/" className="flex items-center gap-2" data-testid="nav-logo" onClick={handleLogoClick}>
            <img src={LOGO_URL} alt="Prodimmo" className="h-9 md:h-11" />
            <span
              className={`text-lg font-semibold tracking-tight transition-colors ${scrolled ? "text-slate-900" : "text-white"}`}
              style={{ fontFamily: 'Outfit' }}
            >
              Prodimmo
            </span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => {
              const id = link.href.slice(1);
              const isActive = activeSection === id;
              return (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  data-testid={`nav-link-${id}`}
                  className={`relative text-sm font-medium transition-colors hover:text-[#73B5E2] ${
                    isActive
                      ? "text-[#73B5E2]"
                      : scrolled ? "text-slate-600" : "text-white/90"
                  }`}
                >
                  {link.label}
                  {isActive && (
                    <motion.div
                      layoutId="nav-active"
                      className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#73B5E2] rounded-full"
                      transition={{ type: "spring", stiffness: 300, damping: 25 }}
                    />
                  )}
                </a>
              );
            })}
            <button
              onClick={toggleLang}
              data-testid="lang-toggle"
              className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full border transition-all hover:scale-105 ${
                scrolled
                  ? "border-slate-200 text-slate-600 hover:border-[#73B5E2] hover:text-[#73B5E2]"
                  : "border-white/30 text-white/90 hover:border-white hover:text-white"
              }`}
            >
              <Globe className="w-3.5 h-3.5" />
              {lang === "fr" ? "EN" : "FR"}
            </button>
          </div>

          <div className="md:hidden flex items-center gap-3">
            <button
              onClick={toggleLang}
              data-testid="lang-toggle-mobile"
              className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border ${
                scrolled
                  ? "border-slate-200 text-slate-600"
                  : "border-white/30 text-white/90"
              }`}
            >
              <Globe className="w-3 h-3" />
              {lang === "fr" ? "EN" : "FR"}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              className="p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? (
                <X className={scrolled ? "text-slate-900" : "text-white"} size={24} />
              ) : (
                <Menu className={scrolled ? "text-slate-900" : "text-white"} size={24} />
              )}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-slate-100"
          >
            <div className="px-4 py-4 space-y-3">
              {navLinks.map((link) => {
                const id = link.href.slice(1);
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className={`block font-medium py-2 ${activeSection === id ? "text-[#73B5E2]" : "text-slate-700 hover:text-[#73B5E2]"}`}
                  >
                    {link.label}
                  </a>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
